import { Injectable, OnDestroy, inject } from '@angular/core';
import { AuthStateService } from '@dental-ui/auth';
import { TranslateService } from '@dental-ui/shared/i18n';
import { Subscription, take } from 'rxjs';
import { ConfirmDialogService } from './confirm-dialog.service';

/**
 * ForceLogout (SignalR veya diğer sekme broadcast) geldiğinde oturumu hemen kapatmaz;
 * "Session ended" dialog gösterir; onay / kapat sonrası logout yapar.
 */
@Injectable({
  providedIn: 'root',
})
export class ForceLogoutDialogService implements OnDestroy {
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly authStateService = inject(AuthStateService);
  private readonly translateService = inject(TranslateService);

  private readonly subscription: Subscription;
  private dialogOpen = false;

  constructor() {
    // SignalR ve diğer-sekme broadcast: AuthStateService.requestForceLogoutDialog()
    // (forceLogoutReceived$ ile merge edilmez — senkron çift next dialog'u hemen kapatır)
    this.subscription = this.authStateService.forceLogoutRequested$.subscribe(() => {
      this.showForceLogoutDialog();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private showForceLogoutDialog(): void {
    // Tekrarlayan SignalR/revoke olayları dialog açıkken hemen logout yapmasın
    if (this.dialogOpen) {
      return;
    }

    if (!this.authStateService.isAuthenticated()) {
      return;
    }

    this.dialogOpen = true;
    const isTr = this.translateService.getCurrentLanguage()?.toLowerCase().startsWith('tr');

    this.confirmDialogService
      .confirm({
        title: this.t(
          'AUTH.FORCE_LOGOUT_TITLE',
          isTr ? 'Oturumunuz sonlandırıldı' : 'Session ended'
        ),
        message: this.t(
          'AUTH.FORCE_LOGOUT_MESSAGE',
          this.t(
            'forceLogoutMessage',
            isTr
              ? 'Hesabınız başka bir cihazda açıldı.'
              : 'Your account was signed in on another device.'
          )
        ),
        confirmText: this.t(
          'AUTH.FORCE_LOGOUT_CONFIRM',
          isTr ? 'Giriş yap' : 'Sign in'
        ),
        hideCancel: true,
        showCloseButton: true,
        confirmButtonType: 'primary',
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.dialogOpen = false;
          this.authStateService.forceRemoteLogout();
        },
        error: () => {
          this.dialogOpen = false;
          this.authStateService.forceRemoteLogout();
        },
      });
  }

  /** getText bulunamayan key'i olduğu gibi döndürdüğü için fallback uygular */
  private t(key: string, fallback: string): string {
    const text = this.translateService.getText(key);
    return !text || text === key ? fallback : text;
  }
}
